import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../lib/supabase';
import { formatBRL } from '../../lib/currency';
import { safeInvoke } from '../../lib/safeInvoke';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { Term } from '../../components/admin/AdminGlossary';
import { CurrencyToggle, useAdminCurrency } from '../../components/admin/CurrencyToggle';
import { CohortRetentionHeatmap } from '../../components/admin/CohortRetentionHeatmap';

interface KPI { label: string; value: string; change: number; sparkData: { v: number }[]; tooltip?: string }
interface ActivityItem { id: string; description: string; relativeTime: string; userId?: string }
interface IntegrationStatus { name: string; status: 'green' | 'yellow' | 'red'; label: string }

interface BusinessHealth {
  mrr:               number;
  arr:               number;
  mrrGrowthPct:      number;
  totalPaidUsers:    number;
  newPaidToday:      number;
  churnedThisMonth:  number;
  churnRatePct:      number;
  arpu:              number;
  estimatedLtv:      number;
  chargebackRatePct: number;
  d1RetentionPct:    number;
  d7RetentionPct:    number;
  d30RetentionPct:   number;
  calculatedAt:      string;
}

function trafficLight(value: number, greenThreshold: number, redThreshold: number, higherIsBetter = true): string {
  if (higherIsBetter) {
    return value >= greenThreshold ? '🟢' : value >= redThreshold ? '🟡' : '🔴';
  } else {
    return value <= greenThreshold ? '🟢' : value <= redThreshold ? '🟡' : '🔴';
  }
}

function KPICard({ kpi }: { kpi: KPI }) {
  const up = kpi.change >= 0;
  return (
    <div className="card" style={{ flex: '1 1 180px' }} title={kpi.tooltip}>
      <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {kpi.label}
        {kpi.tooltip && (
          <span style={{ marginLeft: '0.3rem', opacity: 0.6, cursor: 'help', fontSize: '10px' }} title={kpi.tooltip}>ℹ</span>
        )}
      </p>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', color: 'var(--text-primary)', fontWeight: 700, margin: '0 0 0.4rem' }}>
        {kpi.value}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        {up ? <ArrowUpRight size={14} color="#4ade80" /> : <ArrowDownRight size={14} color="#f87171" />}
        <span style={{ fontSize: 'var(--text-caption)', color: up ? '#4ade80' : '#f87171' }}>
          {up ? '+' : ''}{kpi.change.toFixed(1)}% vs ontem
        </span>
      </div>
      <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: '0.2rem 0 0.25rem', fontStyle: 'italic' }}>
        Comparativo indisponível
      </p>
      <div style={{ height: '32px', marginTop: '0.25rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={kpi.sparkData}>
            <Area type="monotone" dataKey="v" stroke="#F5C842" fill="rgba(245,200,66,0.1)" strokeWidth={1.5} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const STATUS_COLORS = { green: '#4ade80', yellow: '#fbbf24', red: '#f87171' };

export function AdminDashboardPage() {
  const navigate               = useNavigate();
  const { fmt }                = useAdminCurrency();
  const [kpis,      setKpis]   = useState<KPI[]>([]);
  const [revenueData, setRevenueData] = useState<{ date: string; revenue: number }[]>([]);
  const [mrrHistory, setMrrHistory] = useState<{ month: string; mrr_brl: number; paid_users: number }[]>([]);
  const [activity,  setActivity] = useState<ActivityItem[]>([]);
  const [freeProducts, setFreeProducts] = useState<string[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [loading,   setLoading]  = useState(true);
  const [health,    setHealth]   = useState<BusinessHealth | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadBusinessHealth() {
    setHealthLoading(true);
    const { data } = await safeInvoke<{ metrics: BusinessHealth }>('business-metrics', {
      body:     {},
      fallback: null as unknown as { metrics: BusinessHealth },
      timeout:  20_000,
    });
    if (data?.metrics) setHealth(data.metrics);
    setHealthLoading(false);
  }

  async function loadMRRHistory() {
    const { data } = await supabase.rpc('get_mrr_history', { months_back: 12 });
    if (data) setMrrHistory(data as { month: string; mrr_brl: number; paid_users: number }[]);
  }

  async function loadAll() {
    await Promise.all([loadKPIs(), loadRevenue(), loadActivity(), loadBusinessHealth(), loadMRRHistory()]);
    setIntegrations([
      { name: 'Stripe',    status: 'green',  label: 'Online' },
      { name: 'Claude',    status: 'green',  label: 'Online' },
      { name: 'PIX',       status: 'yellow', label: 'Verificar' },
      { name: 'Email',     status: 'green',  label: 'Online' },
      { name: 'Supabase',  status: 'green',  label: 'Online' },
    ]);
    setLoading(false);
  }

  async function loadKPIs() {
    const today     = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);

    const [
      { count: totalUsers }, { count: todayUsers }, { count: yestUsers },
      { count: monthlyCount }, { count: annualCount },
    ] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact', head: true }),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', today + 'T00:00:00Z'),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).gte('created_at', yesterday + 'T00:00:00Z').lt('created_at', today + 'T00:00:00Z'),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('subscription_tier', 'monthly'),
      supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('subscription_tier', 'annual'),
    ]);

    const { data: payments30 } = await supabase
      .from('payments')
      .select('amount_brl')
      .eq('status', 'completed')
      .gte('created_at', new Date(Date.now() - 30 * 86400000).toISOString());
    const rev30 = payments30?.reduce((s, p) => s + (p.amount_brl ?? 0), 0) ?? 0;

    const { data: freeProds } = await supabase
      .from('product_prices')
      .select('id')
      .eq('active', true)
      .eq('price_brl', 0);

    // Build spark data (last 7 days dummy from real counts)
    const spark = Array.from({ length: 7 }, (_, i) => ({ v: Math.max(1, (totalUsers ?? 0) / 7 + (i * 2 - 3)) }));

    setKpis([
      { label: 'Total Usuários',          value: String(totalUsers ?? 0),   change: 2.1,  sparkData: spark, tooltip: 'Número total de usuários cadastrados na plataforma.' },
      { label: 'Novas Hoje',              value: String(todayUsers ?? 0),   change: ((todayUsers ?? 0) - (yestUsers ?? 0)) / Math.max(1, yestUsers ?? 1) * 100, sparkData: spark.map(s => ({ v: s.v / 7 })), tooltip: 'Usuários que se registraram hoje.' },
      { label: 'Mensal',                  value: String(monthlyCount ?? 0), change: 0,    sparkData: Array(7).fill({ v: monthlyCount ?? 0 }), tooltip: 'Assinantes com plano mensal ativo.' },
      { label: 'Anual',                   value: String(annualCount ?? 0),  change: 0,    sparkData: Array(7).fill({ v: annualCount ?? 0 }), tooltip: 'Assinantes com plano anual ativo.' },
      { label: 'Receita 30 Dias',         value: formatBRL(rev30),          change: 5.3,  sparkData: spark.map(s => ({ v: s.v * 24 })), tooltip: 'Total de receita bruta dos últimos 30 dias, incluindo todos os pagamentos aprovados.' },
      { label: 'Usuários Plano Gratuito', value: String(freeProds?.length ?? 0), change: 0, sparkData: Array(7).fill({ v: freeProds?.length ?? 0 }), tooltip: 'Quantidade de produtos com preço zero (plano gratuito).' },
    ]);
    setFreeProducts(freeProds?.map(p => p.id) ?? []);
  }

  async function loadRevenue() {
    const { data } = await supabase
      .from('payments')
      .select('amount_brl, created_at')
      .eq('status', 'completed')
      .gte('created_at', new Date(Date.now() - 14 * 86400000).toISOString())
      .order('created_at');

    if (!data) return;

    // Group by date
    const byDate: Record<string, number> = {};
    data.forEach(p => {
      const d = p.created_at.slice(0, 10);
      byDate[d] = (byDate[d] ?? 0) + (p.amount_brl ?? 0);
    });

    const days: { date: string; revenue: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      days.push({ date: d.slice(5), revenue: byDate[d] ?? 0 });
    }
    setRevenueData(days);
  }

  async function loadActivity() {
    const { data: payments } = await supabase
      .from('payments')
      .select('id, amount_brl, product_price_id, created_at, user_id, product_prices:product_price_id(name)')
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(15);

    if (!payments) return;

    // Fetch profile names
    const userIds = [...new Set(payments.map(p => p.user_id))];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds);
    const nameMap = Object.fromEntries((profiles ?? []).map(p => [p.id, p.full_name ?? 'Usuário']));

    const now = Date.now();
    const items: ActivityItem[] = payments.map(p => {
      const name    = nameMap[p.user_id] ?? 'Usuário';
      const initials = name.split(' ').map((n: string) => n[0]).slice(0, 2).join('');
      const product  = (p.product_prices as unknown as { name?: string } | null)?.name ?? p.product_price_id;
      const diff     = now - new Date(p.created_at).getTime();
      const relTime  = diff < 60000 ? 'agora' : diff < 3600000 ? `${Math.floor(diff / 60000)} min` : diff < 86400000 ? `${Math.floor(diff / 3600000)}h` : `${Math.floor(diff / 86400000)}d`;

      return {
        id: p.id,
        userId: p.user_id,
        description: `${initials}. assinou ${product} · ${formatBRL(p.amount_brl ?? 0)} · ${relTime}`,
        relativeTime: relTime,
      };
    });
    setActivity(items);
  }

  if (loading) {
    return (
      <div>
        <AdminPageHeader
          title="Dashboard"
          objective="Visão geral em tempo real da saúde do negócio: receita, usuários, integrações e alertas críticos. Os números são atualizados automaticamente a cada carregamento."
        />
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4].map(i => <div key={i} className="card shimmer" style={{ flex: '1 1 180px', height: '120px' }} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Dashboard"
        objective="Visão geral em tempo real da saúde do negócio: receita, usuários, integrações e alertas críticos. Os números são atualizados automaticamente a cada carregamento."
      />

      {/* Currency toggle near revenue KPIs */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
        <CurrencyToggle />
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {kpis.map(k => <KPICard key={k.label} kpi={k} />)}
      </div>

      {/* Free products alert */}
      {freeProducts.length > 0 && (
        <div style={{ padding: '0.875rem 1rem', backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={16} color="#fbbf24" />
            <span style={{ fontSize: 'var(--text-label)', color: '#fbbf24' }}>
              Produtos gratuitos: <strong>{freeProducts.join(', ')}</strong>
            </span>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin-ap/pricing')}>
            Configurar todos →
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem', alignItems: 'start' }}>
        {/* Revenue chart */}
        <div className="card">
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Receita, 14 dias</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={revenueData}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `R$${v}`} />
              <Tooltip formatter={(v: number) => fmt(v)} labelStyle={{ color: 'var(--text-muted)' }} contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }} />
              <Area type="monotone" dataKey="revenue" stroke="#F5C842" fill="rgba(245,200,66,0.12)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Activity feed */}
        <div className="card" style={{ maxHeight: '280px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', flexShrink: 0 }}>Atividade recente</p>
          <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '0' }}>
            {activity.map(item => (
              <button key={item.id} onClick={() => item.userId && navigate(`/admin-ap/users?id=${item.userId}`)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '0.5rem 0', borderBottom: '1px solid var(--border-subtle)', display: 'block', width: '100%' }}>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.4 }}>{item.description}</p>
              </button>
            ))}
            {activity.length === 0 && (
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma atividade ainda</p>
            )}
          </div>
        </div>
      </div>

      {/* Integration health strip */}
      <div className="card" style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: 0 }}>Status das integrações</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {integrations.map(int => (
            <button key={int.name} onClick={() => navigate('/admin-ap/integrations')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: 0 }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: STATUS_COLORS[int.status] }} />
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{int.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* C27: Business Health Dashboard */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: 'var(--text-label)' }}>
            📊 Saúde do Negócio
          </p>
          <button className="btn btn-secondary btn-sm" onClick={loadBusinessHealth} disabled={healthLoading}>
            {healthLoading ? 'Calculando…' : 'Atualizar'}
          </button>
        </div>

        {healthLoading && !health && (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Calculando métricas…</p>
        )}

        {health && (
          <div>
            {/* Traffic light alerts */}
            {(health.churnRatePct > 5 || health.chargebackRatePct > 0.5 || health.mrrGrowthPct < 0) && (
              <div style={{ padding: '0.75rem 1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={14} color="#f87171" />
                <span style={{ fontSize: 'var(--text-caption)', color: '#f87171' }}>
                  {health.chargebackRatePct > 0.5 ? `⚠️ Chargeback rate ${health.chargebackRatePct.toFixed(2)}% acima do limite de 0.5%!` : ''}
                  {health.churnRatePct > 5 ? ` ⚠️ Churn rate ${health.churnRatePct}% elevado.` : ''}
                  {health.mrrGrowthPct < 0 ? ` ⚠️ MRR declinando ${health.mrrGrowthPct.toFixed(1)}% MoM.` : ''}
                </span>
              </div>
            )}

            {/* Revenue metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              {[
                { label: 'MRR',            value: fmt(health.mrr),             light: trafficLight(health.mrrGrowthPct, 10, 0),                termKey: 'MRR' as const },
                { label: 'ARR',            value: fmt(health.arr),             light: '',                                                       termKey: 'ARR' as const },
                { label: 'Crescimento MoM', value: `${health.mrrGrowthPct > 0 ? '+' : ''}${health.mrrGrowthPct.toFixed(1)}%`, light: trafficLight(health.mrrGrowthPct, 10, 0), termKey: null },
                { label: 'Usuários Pagos', value: String(health.totalPaidUsers), light: '',                                                      termKey: null },
                { label: 'Novos Hoje',     value: String(health.newPaidToday),  light: '',                                                       termKey: null },
                { label: 'ARPU',           value: fmt(health.arpu),            light: '',                                                       termKey: 'ARPU' as const },
                { label: 'LTV Estimado',   value: fmt(health.estimatedLtv),    light: '',                                                       termKey: 'LTV' as const },
                { label: 'Churn Rate',     value: `${health.churnRatePct.toFixed(1)}%`, light: trafficLight(health.churnRatePct, 2, 5, false),  termKey: 'Churn' as const },
                { label: 'Chargebacks',    value: `${health.chargebackRatePct.toFixed(2)}%`, light: trafficLight(health.chargebackRatePct, 0.5, 1, false), termKey: null },
              ].map(item => (
                <div key={item.label} style={{ backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {item.light} {item.termKey ? <Term k={item.termKey}>{item.label}</Term> : item.label}
                  </p>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', color: 'var(--text-primary)', fontWeight: 700, margin: 0 }}>
                    {item.value}
                  </p>
                  <p style={{ fontSize: '10px', color: 'var(--text-muted)', margin: '0.15rem 0 0', fontStyle: 'italic' }}>vs mês anterior</p>
                </div>
              ))}
            </div>

            {/* Retention */}
            <div>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Retenção por coorte
              </p>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'D1',  value: health.d1RetentionPct,  threshold: 30, warn: 15 },
                  { label: 'D7',  value: health.d7RetentionPct,  threshold: 20, warn: 10 },
                  { label: 'D30', value: health.d30RetentionPct, threshold: 10, warn: 5  },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{r.label}</span>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                      {trafficLight(r.value, r.threshold, r.warn)} {r.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.75rem', marginBottom: 0 }}>
              Calculado em {new Date(health.calculatedAt).toLocaleString('pt-BR')} · Atualiza a cada 6h
            </p>
          </div>
        )}
      </div>

      {/* MRR History, 12 months */}
      {mrrHistory.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem' }}>Receita mensal, 12 meses</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={mrrHistory}>
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `R$${v}`} />
              <Tooltip
                formatter={(v: number) => fmt(v)}
                labelStyle={{ color: 'var(--text-muted)' }}
                contentStyle={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '6px', fontSize: '12px' }}
              />
              <Area type="monotone" dataKey="mrr_brl" stroke="#C9A84C" fill="rgba(201,168,76,0.1)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cohort retention heatmap */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <CohortRetentionHeatmap />
      </div>
    </div>
  );
}
